/**
 * CR-863: "Supplier price change requests" on Cost › Historical.
 *
 * How many times suppliers asked for a new price on a PO line through the
 * portal, per week or month (the dashboard's View switch), stacked by supplier:
 * the six with the most requests each get a colour and the rest share "Other
 * suppliers". ERP price changes and the customer's acceptances are not counted.
 * A supplier sees only their own requests, as one series. Clicking a segment
 * opens that supplier's (or Other's) requests in that period.
 *
 * CR-8627: `variant="customer"` draws the customer side the same way, "Customer
 * price changes": the price changes the customer's ERP sent through the live
 * link. Customer portal only (the backend sends no series to a supplier).
 */
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { PriceChangeRequestsSeries, PriceChangeRequestsSeriesItem } from '../../../lib/api';
import { usePortalMode } from '../../../hooks/usePortalMode';
import { CHART_COLORS } from '../../../pages/report/constants';
import { ChartWrapper } from '../ChartWrapper';
import { PriceChangeRequestsModal } from './PriceChangeRequestsModal';
import { CustomerPriceChangesModal } from './CustomerPriceChangesModal';

/** "Other suppliers" is neutral, so it never reads as one more supplier. */
const OTHER_COLOR = '#94a3b8';
const OWN_SERIES_NAME = 'Your price change requests';

/** CR-8627: the customer chart's words; the supplier chart keeps CR-863's below. */
const CUSTOMER_TEXT = {
  title: (unit: string) => `Customer price changes per ${unit}`,
  subtitle: (unit: string) =>
    `Each bar counts the price changes your ERP sent that ${unit}, split by supplier. Click a bar to see the changes.`,
  totalLabel: 'changes in the period',
  emptyTitle: 'No price changes from your ERP in this period',
  emptyBody: 'A change appears here when your ERP sends a new price on one of your PO lines.',
  clickHint: 'Click to see the changes',
};

interface PriceChangeRequestsChartProps {
  data: PriceChangeRequestsSeries;
  /** The query the dashboard was fetched with; the click-through re-sends it. */
  statsParams: Record<string, string | number | boolean>;
  /** CR-8627: `customer` draws the ERP price changes; default the supplier requests (CR-863). */
  variant?: 'supplier' | 'customer';
}

interface Segment {
  series: PriceChangeRequestsSeriesItem;
  index: number;
}

/**
 * The stacked bar chart and its click-through.
 *
 * @param data - `costData.price_change_requests` from `dashboard_stats`
 * @param statsParams - the dashboard's own query, for the click-through
 * @param variant - `customer` for the ERP price changes (CR-8627)
 */
export function PriceChangeRequestsChart({ data, statsParams, variant = 'supplier' }: PriceChangeRequestsChartProps) {
  // Supplier portal: one series ("Your price change requests"), no supplier legend.
  // The customer chart is never sent to a supplier, so it always keeps its legend.
  const customer = variant === 'customer';
  const isSupplier = usePortalMode().isSupplier && !customer;
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  // recharts 3 hands a `shared={false}` tooltip the FIRST series whichever bar
  // is hovered (found on CR-1421), so the chart tracks the hovered segment itself.
  const [hovered, setHovered] = useState<Segment | null>(null);
  const [opened, setOpened] = useState<Segment | null>(null);

  const weekly = data.period === 'weekly';
  const unit = weekly ? 'week' : 'month';
  const colorOf = (s: PriceChangeRequestsSeriesItem, i: number) =>
    s.key === 'other' ? OTHER_COLOR : CHART_COLORS[i % CHART_COLORS.length];
  const nameOf = (s: PriceChangeRequestsSeriesItem) => (isSupplier ? OWN_SERIES_NAME : s.name);

  const rows = useMemo(
    () => data.points.map((p) => {
      const row: Record<string, string | number> = { label: p.label, total: p.total };
      data.series.forEach((s) => { row[s.key] = p.counts[s.key] ?? 0; });
      return row;
    }),
    [data],
  );

  const shown = data.series.filter((s) => visible[nameOf(s)] !== false);
  const legend = isSupplier ? undefined : data.series.map((s, i) => ({ color: colorOf(s, i), label: s.name, dataKey: s.key }));
  const point = opened ? data.points[opened.index] : null;

  return (
    <>
      <ChartWrapper
        title={customer ? CUSTOMER_TEXT.title(unit)
          : isSupplier ? `Your price change requests per ${unit}` : `Supplier price change requests per ${unit}`}
        subtitle={customer ? CUSTOMER_TEXT.subtitle(unit) : isSupplier
          ? `Each bar counts the price changes you asked for that ${unit}. Click a bar to see them.`
          : `Each bar counts the requests suppliers raised that ${unit}, split by supplier. Click a bar to see the requests.`}
        legend={legend}
        visibleSeries={isSupplier ? undefined : visible}
        onSeriesToggle={isSupplier ? undefined : (label, on) => setVisible((v) => ({ ...v, [label]: on }))}
        headerActions={(
          <div className="text-right leading-tight mr-2">
            <div className="text-2xl font-bold tabular-nums">{data.total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{customer ? CUSTOMER_TEXT.totalLabel : 'requests in the period'}</div>
          </div>
        )}
        // An empty array would swap our empty state for the wrapper's generic one.
        data={data.total > 0 ? rows : undefined}
        exportFilename={customer ? 'customer-price-changes' : 'price-change-requests'}
      >
        {data.total === 0 ? (
          <div className="h-[280px] flex flex-col items-center justify-center text-center px-6">
            <p className="text-sm font-medium">{customer ? CUSTOMER_TEXT.emptyTitle : 'No price change requests in this period'}</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              {customer ? CUSTOMER_TEXT.emptyBody
                : 'A request appears here when a supplier proposes a new price on one of your PO lines through the portal.'}
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} onMouseLeave={() => setHovered(null)}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={weekly ? 'preserveStartEnd' : 0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip
                cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
                content={() => (hovered ? (
                  <SegmentTooltip
                    period={data.points[hovered.index]?.label ?? ''}
                    name={nameOf(hovered.series)}
                    count={data.points[hovered.index]?.counts[hovered.series.key] ?? 0}
                    total={data.points[hovered.index]?.total ?? 0}
                    unit={unit}
                    hint={customer ? CUSTOMER_TEXT.clickHint : 'Click to see the requests'}
                  />
                ) : null)}
              />
              {shown.map((s) => {
                const i = data.series.indexOf(s);
                return (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={nameOf(s)}
                    stackId={customer ? 'changes' : 'requests'}
                    fill={colorOf(s, i)}
                    cursor="pointer"
                    onMouseEnter={(_: unknown, index: number) => setHovered({ series: s, index })}
                    onClick={(_: unknown, index: number) => {
                      if ((data.points[index]?.counts[s.key] ?? 0) > 0) setOpened({ series: s, index });
                    }}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      {opened && point && (customer ? (
        <CustomerPriceChangesModal
          open
          onClose={() => setOpened(null)}
          title={`${point.label} · ${nameOf(opened.series)}`}
          query={{
            scope: statsParams,
            periodStart: point.period_start,
            periodEnd: point.period_end,
            supplierIds: opened.series.supplier_ids,
          }}
        />
      ) : (
        <PriceChangeRequestsModal
          open
          onClose={() => setOpened(null)}
          title={`${point.label} · ${nameOf(opened.series)}`}
          query={{
            scope: statsParams,
            periodStart: point.period_start,
            periodEnd: point.period_end,
            supplierIds: opened.series.supplier_ids,
          }}
        />
      ))}
    </>
  );
}

interface SegmentTooltipProps {
  period: string;
  name: string;
  count: number;
  total: number;
  unit: string;
  hint: string;
}

function SegmentTooltip({ period, name, count, total, unit, hint }: SegmentTooltipProps) {
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md min-w-[180px]">
      <div className="font-semibold mb-1">{period}</div>
      <div className="flex justify-between gap-4"><span>{name}</span><span className="font-semibold tabular-nums">{count}</span></div>
      <div className="flex justify-between gap-4 text-muted-foreground"><span>All suppliers this {unit}</span><span className="tabular-nums">{total}</span></div>
      <div className="text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}
